import Link from "next/link";
import { useEffect, useRef } from "react";
import useTranslation from "next-translate/useTranslation";
import { FaArrowRight } from "react-icons/fa";
import { useUser } from "@/redux/user";
import { trackEvent } from "@/services/eventTracker";
import useLearningOverview from "../learning/useLearningOverview";
import { learningPromptKey, learningTopicKey } from "../learning/learningTypes";

/** A secondary invitation; progress and rewards live in the learning overview. */
export default function DailyPracticeCard() {
  const { t } = useTranslation("common");
  const { memberLoggedIn } = useUser();
  const { data } = useLearningOverview();
  const link = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (!data || !link.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      void trackEvent("daily_practice_offered", {
        entrySource: "lobby",
        offer: data.nextStep.kind,
        completedToday: data.practice.completedToday,
        flowVersion: 1,
        audience: memberLoggedIn ? "member" : "guest",
      });
    });
    observer.observe(link.current);
    return () => observer.disconnect();
  }, [
    memberLoggedIn,
    data?.nextStep.kind,
    data?.nextStep.isNew,
    data?.nextStep.lessonSlug,
    data?.practice.completedToday,
  ]);
  if (!data) return null;
  return (
    <Link
      ref={link}
      href="/learn"
      className="flex min-h-[44px] items-center justify-between gap-3 px-1 text-xs leading-5 text-slate-500 hover:text-altblue-600"
      onClick={() =>
        void trackEvent("daily_practice_selected", {
          entrySource: "lobby",
          offer: data.nextStep.kind,
          completedToday: data.practice.completedToday,
          flowVersion: 1,
          audience: memberLoggedIn ? "member" : "guest",
        })
      }
    >
      <span>
        {t(learningPromptKey(data), {
          topic: t(learningTopicKey(data.nextStep.category)),
        })}
      </span>
      <FaArrowRight aria-hidden="true" className="shrink-0 text-[10px]" />
    </Link>
  );
}
